const METERS_TO_FEET = 3.28084;
const FEET_PER_MILE = 5280;

export const WALKING_METERS_PER_SECOND = 1.0;

export function quantizeDistanceFeet(feet: number): number {
  if (feet < 10) {
    return Math.round(feet);
  }
  if (feet < 50) {
    return Math.round(feet / 2) * 2;
  }
  if (feet < 100) {
    return Math.round(feet / 5) * 5;
  }
  return Math.round(feet / 10) * 10;
}

export function formatDistance(meters: number): string {
  if (!Number.isFinite(meters)) {
    return '—';
  }
  const feet = quantizeDistanceFeet(meters * METERS_TO_FEET);
  return `${feet} ft`;
}

export function formatDistanceShort(
  meters: number,
  unit: 'imperial' | 'metric' = 'imperial',
): string {
  if (!Number.isFinite(meters)) {
    return '—';
  }
  if (unit === 'imperial') {
    const feet = meters * METERS_TO_FEET;
    if (feet < 528) {
      return `${Math.round(feet)} ft`;
    }
    const miles = feet / FEET_PER_MILE;
    if (miles < 10) {
      return `${miles.toFixed(1)} mi`;
    }
    return `${Math.round(miles)} mi`;
  }
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  const km = meters / 1000;
  if (km < 10) {
    return `${km.toFixed(1)} km`;
  }
  return `${Math.round(km)} km`;
}

/** UWB-precise distance label (±10 cm); one decimal under 10 m. */
export function formatUwbDistanceShort(meters: number): string {
  if (!Number.isFinite(meters)) {
    return '—';
  }
  if (meters < 10) {
    return `${meters.toFixed(1)} m`;
  }
  return formatDistanceShort(meters, 'metric');
}

export function formatUwbDistanceShortWithWalkTime(meters: number): string {
  if (!Number.isFinite(meters)) {
    return '—';
  }
  return `${formatUwbDistanceShort(meters)} · ${formatWalkingTimeShort(meters)}`;
}

export function formatUwbDistanceWithWalkTime(meters: number): string {
  if (!Number.isFinite(meters)) {
    return '—';
  }
  return `${formatUwbDistanceShort(meters)} · ${formatWalkingTime(meters)}`;
}

export function formatWalkingTime(distanceMeters: number): string {
  if (!Number.isFinite(distanceMeters) || distanceMeters <= 0) {
    return '—';
  }
  const seconds = distanceMeters / WALKING_METERS_PER_SECOND;
  const minutes = seconds / 60;
  if (minutes < 1) {
    return '1 min';
  }
  if (minutes < 60) {
    return `${Math.round(minutes)} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  if (remainingMinutes === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${remainingMinutes} min`;
}

export function formatWalkingTimeShort(distanceMeters: number): string {
  if (!Number.isFinite(distanceMeters) || distanceMeters <= 0) {
    return '—';
  }
  const seconds = distanceMeters / WALKING_METERS_PER_SECOND;
  const minutes = seconds / 60;
  if (minutes < 1) {
    return '1 min';
  }
  if (minutes < 60) {
    return `${Math.round(minutes)} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  if (remainingMinutes === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${remainingMinutes} min`;
}

export function formatDistanceShortWithWalkTime(meters: number): string {
  if (!Number.isFinite(meters)) {
    return '—';
  }
  return `${formatDistanceShort(meters)} · ${formatWalkingTimeShort(meters)}`;
}

export function formatDistanceWithWalkTime(meters: number): string {
  if (!Number.isFinite(meters)) {
    return '—';
  }
  return `${formatDistance(meters)} · ${formatWalkingTime(meters)}`;
}
